import { useQuery, useMutation, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { axiosInstance } from '@/lib/api';
import { ApiResponse, ApiError } from '@/lib/api/types';
import { AxiosError } from 'axios';
import { toast } from 'react-hot-toast';

export function normalizeApiError(error: any): string {
  const errorData = error?.response?.data as any;
  const detail = errorData?.detail ?? errorData?.message ?? error?.message;

  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const msg = (item as any).msg ?? (item as any).message;
          const loc = (item as any).loc;
          const field =
            Array.isArray(loc) && loc.length > 0
              ? loc
                  .filter((x: any) => x !== 'body')
                  .map((x: any) => String(x))
                  .join('.')
              : '';
          if (typeof msg === 'string') return field ? `${field}: ${msg}` : msg;
          return null;
        }
        return null;
      })
      .filter((m): m is string => Boolean(m));

    if (messages.length > 0) return Array.from(new Set(messages)).join(', ');
  }

  if (detail && typeof detail === 'object') {
    const msg = (detail as any).msg ?? (detail as any).message;
    if (typeof msg === 'string') return msg;
  }

  return 'Có lỗi xảy ra';
}

// Extended options for mutations
export type ApiMutationOptions<TData = any, TVariables = any, TError = ApiError> = UseMutationOptions<ApiResponse<TData>, AxiosError<TError>, TVariables, unknown> & {
  errorMap?: Record<string, string>;
  errorMessage?: string; // Fallback generic message
  showErrorToast?: boolean; // Default true
};

// Generic hook cho GET requests
export function useApiQuery<TData = any, TError = ApiError>(
  queryKey: string[],
  url: string,
  options?: Omit<UseQueryOptions<ApiResponse<TData>, AxiosError<TError>, ApiResponse<TData>, string[]>, 'queryKey'>
) {
  return useQuery({
    queryKey,
    queryFn:
      options?.queryFn ??
      (async () => {
        const response = await axiosInstance.get<ApiResponse<TData>>(url);
        return response.data;
      }),
    ...options,
  });
}

// Generic hook cho POST requests
export function useApiMutation<TData = any, TVariables = any, TError = ApiError>(
  url: string,
  options?: ApiMutationOptions<TData, TVariables, TError>
) {
  const { onError, onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: async (variables: TVariables) => {
      const response = await axiosInstance.post<ApiResponse<TData>>(url, variables);
      return response.data;
    },
    onSuccess: (data, variables, context, mutation) => {
      onSuccess?.(data, variables, context, mutation);
    },
    onError: (error, variables, context, mutation) => {
      if (options?.showErrorToast !== false) {
        const errorData = error.response?.data as any;
        // Ưu tiên detail (FastAPI) hoặc message
        const code = errorData?.detail || errorData?.message;
        const message =
          (typeof code === 'string' ? options?.errorMap?.[code] : undefined) ??
          options?.errorMessage ??
          normalizeApiError(error);
        
        // Không toast nếu là 401/403/500 (đã xử lý ở axios interceptor)
        // Tuy nhiên interceptor trả về Promise.reject(error), nên flow vẫn chạy vào đây.
        // Cần check status để tránh toast 2 lần hoặc toast thừa.
        const status = error.response?.status;
        if (options?.showErrorToast === true || (status !== 401 && status !== 403 && status !== 500)) {
           toast.error(message);
        }
      }
      onError?.(error, variables, context, mutation);
    },
    ...restOptions,
  });
}

// Generic hook cho PUT requests
export function useApiPut<TData = any, TVariables = any, TError = ApiError>(
  url: string,
  options?: ApiMutationOptions<TData, TVariables, TError>
) {
  const { onError, onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: async (variables: TVariables) => {
      const response = await axiosInstance.put<ApiResponse<TData>>(url, variables);
      return response.data;
    },
    onSuccess: (data, variables, context, mutation) => {
      onSuccess?.(data, variables, context, mutation);
    },
    onError: (error, variables, context, mutation) => {
      if (options?.showErrorToast !== false) {
        const errorData = error.response?.data as any;
        const code = errorData?.detail || errorData?.message;
        const message =
          (typeof code === 'string' ? options?.errorMap?.[code] : undefined) ??
          options?.errorMessage ??
          normalizeApiError(error);
        
        const status = error.response?.status;
        if (status !== 401 && status !== 403 && status !== 500) {
           toast.error(message);
        }
      }
      onError?.(error, variables, context, mutation);
    },
    ...restOptions,
  });
}

// Generic hook cho PATCH requests
export function useApiPatch<TData = any, TVariables = any, TError = ApiError>(
  url: string,
  options?: ApiMutationOptions<TData, TVariables, TError>
) {
  const { onError, onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: async (variables: TVariables) => {
      const response = await axiosInstance.patch<ApiResponse<TData>>(url, variables);
      return response.data;
    },
    onSuccess: (data, variables, context, mutation) => {
      onSuccess?.(data, variables, context, mutation);
    },
    onError: (error, variables, context, mutation) => {
      if (options?.showErrorToast !== false) {
        const errorData = error.response?.data as any;
        const code = errorData?.detail || errorData?.message;
        const message =
          (typeof code === 'string' ? options?.errorMap?.[code] : undefined) ??
          options?.errorMessage ??
          normalizeApiError(error);
        
        const status = error.response?.status;
        if (status !== 401 && status !== 403 && status !== 500) {
           toast.error(message);
        }
      }
      onError?.(error, variables, context, mutation);
    },
    ...restOptions,
  });
}

// Generic hook cho DELETE requests
export function useApiDelete<TData = any, TError = ApiError>(
  url: string,
  options?: ApiMutationOptions<TData, void, TError>
) {
  const { onError, onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.delete<ApiResponse<TData>>(url);
      return response.data;
    },
    onSuccess: (data, variables, context, mutation) => {
      onSuccess?.(data, variables, context, mutation);
    },
    onError: (error, variables, context, mutation) => {
      if (options?.showErrorToast !== false) {
        const errorData = error.response?.data as any;
        const code = errorData?.detail || errorData?.message;
        const message =
          (typeof code === 'string' ? options?.errorMap?.[code] : undefined) ??
          options?.errorMessage ??
          normalizeApiError(error);
        
        const status = error.response?.status;
        if (status !== 401 && status !== 403 && status !== 500) {
           toast.error(message);
        }
      }
      onError?.(error, variables, context, mutation);
    },
    ...restOptions,
  });
}
